import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  createPost,
  deletePost,
  editPost,
  getAllPosts,
} from "../services/blogsApi";

// Password for admin access (you can change this)
const ADMIN_PASSWORD = "admin123"; // Change this to your desired password

function DashboardPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const [content, setContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlogId, setCurrentBlogId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    blogId: null,
    blogName: "",
  });

  // Fetch blogs function
  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await getAllPosts(1, 100, "");
      setBlogs(response.data || []);
    } catch (error) {
      console.error("Error fetching blogs: ", error);
      showSnackbar("Error fetching blogs", "error");
    } finally {
      setLoading(false);
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const savedAuth = sessionStorage.getItem("admin_authenticated");
    if (savedAuth === "true") {
      setIsAuthenticated(true);
      fetchBlogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle password authentication
  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError(false);
      sessionStorage.setItem("admin_authenticated", "true");
      fetchBlogs();
      showSnackbar("Access granted!", "success");
    } else {
      setPasswordError(true);
      showSnackbar("Incorrect password!", "error");
      setPassword("");
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("admin_authenticated");
    setPassword("");
    showSnackbar("Logged out successfully", "info");
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Add a new blog
  const handleAddBlog = async () => {
    if (!name.trim() || !content.trim()) {
      showSnackbar("Please fill in all required fields", "warning");
      return;
    }

    setLoading(true);
    try {
      const newBlog = {
        name: name.trim(),
        image:
          image.trim() || "https://via.placeholder.com/800x400?text=Blog+Image",
        content,
        createdAt: new Date().toISOString(),
      };

      await createPost(newBlog);
      await fetchBlogs();
      setName("");
      setImage("");
      setContent("");
      showSnackbar("Blog added successfully!", "success");
    } catch (error) {
      console.error("Error adding blog: ", error);
      showSnackbar("Error adding blog", "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete a blog
  const handleDeleteBlog = async () => {
    if (!deleteDialog.blogId) return;

    setLoading(true);
    try {
      await deletePost(deleteDialog.blogId);
      await fetchBlogs();
      setDeleteDialog({ open: false, blogId: null, blogName: "" });
      showSnackbar("Blog deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting blog: ", error);
      showSnackbar("Error deleting blog", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit blog click
  const handleEditBlog = (blog) => {
    setIsEditing(true);
    setCurrentBlogId(blog.id);
    setName(blog.name);
    setImage(blog.image);
    setContent(blog.content);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setCurrentBlogId(null);
    setName("");
    setImage("");
    setContent("");
  };

  // Handle saving edited blog
  const handleSaveEdit = async () => {
    if (!name.trim() || !content.trim()) {
      showSnackbar("Please fill in all required fields", "warning");
      return;
    }

    setLoading(true);
    try {
      const updatedBlog = {
        id: currentBlogId,
        name: name.trim(),
        image:
          image.trim() || "https://via.placeholder.com/800x400?text=Blog+Image",
        content,
        createdAt: new Date().toISOString(),
      };

      await editPost(currentBlogId, updatedBlog);
      await fetchBlogs();
      handleCancelEdit();
      showSnackbar("Blog updated successfully!", "success");
    } catch (error) {
      console.error("Error updating blog: ", error);
      showSnackbar("Error updating blog", "error");
    } finally {
      setLoading(false);
    }
  };

  // Rich text editor modules
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [{ script: "sub" }, { script: "super" }],
      [{ direction: "rtl" }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["link", "image", "video", "formula"],
      ["code-block"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "script",
    "direction",
    "color",
    "background",
    "align",
    "link",
    "image",
    "video",
    "formula",
    "code-block",
  ];

  // Password protection screen
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "2rem",
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: "100%",
            padding: "2rem",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <LockIcon sx={{ fontSize: 60, color: "#667eea", mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Admin Access
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter password to access the admin dashboard
            </Typography>
          </Box>

          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleLogin();
              }
            }}
            error={passwordError}
            helperText={passwordError ? "Incorrect password" : ""}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "0.75rem",
              fontSize: "1rem",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              },
            }}
          >
            Access Dashboard
          </Button>
        </Card>
      </Box>
    );
  }

  // Main dashboard
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)",
        color: "#fff",
        padding: { xs: "1rem", md: "2rem" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Blog Admin Dashboard
        </Typography>
        <Button
          variant="outlined"
          onClick={handleLogout}
          sx={{
            borderColor: "rgba(255, 255, 255, 0.3)",
            color: "#fff",
            "&:hover": {
              borderColor: "rgba(255, 255, 255, 0.5)",
              background: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          Logout
        </Button>
      </Box>

      {/* Add/Edit Blog Form */}
      <Card
        sx={{
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          mb: 4,
          padding: { xs: "1.5rem", md: "2rem" },
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            {isEditing ? (
              <>
                <EditIcon sx={{ color: "#667eea" }} />
                <Typography variant="h5" fontWeight="bold">
                  Edit Blog
                </Typography>
              </>
            ) : (
              <>
                <AddIcon sx={{ color: "#667eea" }} />
                <Typography color="white" variant="h5" fontWeight="bold">
                  Add New Blog
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Blog Title"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />

            <TextField
              fullWidth
              label="Image URL"
              value={image}
              onChange={(e) => setImage(e.target.value)}
              placeholder="https://example.com/image.jpg"
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                },
              }}
            />

            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, color: "rgba(255, 255, 255, 0.7)" }}
              >
                Content (Rich Text Editor)
              </Typography>
              <Box
                sx={{
                  "& .ql-container": {
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    color: "#fff",
                    minHeight: "300px",
                    fontSize: "16px",
                  },
                  "& .ql-editor": {
                    color: "#fff",
                    minHeight: "300px",
                  },
                  "& .ql-toolbar": {
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    "& .ql-stroke": {
                      stroke: "#fff",
                    },
                    "& .ql-fill": {
                      fill: "#fff",
                    },
                    "& .ql-picker-label": {
                      color: "#fff",
                    },
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Start writing your blog content here..."
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              {isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    startIcon={<CancelIcon />}
                    sx={{
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      color: "#fff",
                      "&:hover": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                        background: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveEdit}
                    disabled={loading}
                    startIcon={<SaveIcon />}
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                      },
                    }}
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleAddBlog}
                  disabled={loading}
                  startIcon={<AddIcon />}
                  fullWidth
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    padding: "0.75rem",
                    fontSize: "1rem",
                    fontWeight: 600,
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                    },
                  }}
                >
                  {loading ? "Adding..." : "Add Blog"}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Blogs Table */}
      <Card
        sx={{
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <CardContent>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
            All Blogs ({blogs.length})
          </Typography>

          {(() => {
            if (loading && blogs.length === 0) {
              return (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography>Loading blogs...</Typography>
                </Box>
              );
            }
            if (blogs.length === 0) {
              return (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color="text.secondary">
                    No blogs found. Add your first blog above!
                  </Typography>
                </Box>
              );
            }
            return (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Title
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Image
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                        Created
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "#fff", fontWeight: 600 }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blogs.map((blog) => (
                      <TableRow
                        key={blog.id}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                          },
                        }}
                      >
                        <TableCell sx={{ color: "#fff", maxWidth: 300 }}>
                          <Typography noWrap>{blog.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="img"
                            src={blog.image}
                            alt={blog.name}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 1,
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                            }}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/80x80?text=No+Image";
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                          {blog.createdAt
                            ? new Date(blog.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            {(() => {
                              if (isEditing && currentBlogId === blog.id) {
                                return (
                                  <Chip
                                    label="Editing"
                                    color="primary"
                                    size="small"
                                    sx={{
                                      background: "rgba(102, 126, 234, 0.2)",
                                    }}
                                  />
                                );
                              }
                              return (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      onClick={() => handleEditBlog(blog)}
                                      sx={{
                                        color: "#667eea",
                                        "&:hover": {
                                          background:
                                            "rgba(102, 126, 234, 0.2)",
                                        },
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      onClick={() =>
                                        setDeleteDialog({
                                          open: true,
                                          blogId: blog.id,
                                          blogName: blog.name,
                                        })
                                      }
                                      sx={{
                                        color: "#f44336",
                                        "&:hover": {
                                          background: "rgba(244, 67, 54, 0.2)",
                                        },
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              );
                            })()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({ open: false, blogId: null, blogName: "" })
        }
        PaperProps={{
          sx: {
            background: "rgba(27, 27, 39, 0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "#fff",
          },
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>"{deleteDialog.blogName}"</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, blogId: null, blogName: "" })
            }
            sx={{ color: "#fff" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBlog}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DashboardPage;
